// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import ClientOrderService, { ClientQuotation } from '../../service/clientOrder.service';

export default function QuotationsPage() {
  const [quotations, setQuotations] = useState<ClientQuotation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [selectedQuote, setSelectedQuote] = useState<ClientQuotation | null>(null);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const data = await ClientOrderService.getQuotations();
      setQuotations(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const filtered = quotations.filter((q) =>
    q.quotationCode.toLowerCase().includes(search.toLowerCase()) ||
    q.productName.toLowerCase().includes(search.toLowerCase()) ||
    q.company.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RequestQuoteIcon sx={{ color: '#A77B58', fontSize: 26 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#3B3027' }}>
              Follow-Ups • Quotations
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#756B63', mt: 0.5 }}>
            Sub-menu managing formal commercial quotes, supplier pricing, validity, and contract terms
          </Typography>
        </Box>

        <Chip
          label={`${quotations.length} Active Quotations`}
          sx={{ bgcolor: '#FAF6F0', color: '#5F4B3B', fontWeight: 700, border: '1px solid #E8DFD5' }}
        />
      </Box>

      {/* Search Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #E8DFD5',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search quotations by quote #, product name, supplier company..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 500 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#A77B58' }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="outlined"
          size="small"
          onClick={fetchQuotations}
          startIcon={<RefreshIcon />}
          sx={{
            borderColor: '#D6CEC4',
            color: '#5F4B3B',
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          Refresh
        </Button>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #E8DFD5', overflow: 'hidden' }}>
        <Table sx={{ minWidth: 850 }}>
          <TableHead sx={{ bgcolor: '#FAF6F0' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Quote ID</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Product & Supplier</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Quantity & Quoted Rate</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Valid Until</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Shipment / Terms</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px', textAlign: 'center' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ color: '#A77B58' }} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: '#756B63' }}>No quotations found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ fontWeight: 700, fontSize: '13px' }}>
                    {item.quotationCode}
                    <Typography sx={{ fontSize: '11px', color: '#9E948C' }}>{item.createdAt}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: '#3B3027' }}>
                      {item.productName}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#756B63' }}>
                      {item.company}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#059669' }}>
                      {item.currency} {item.price.toFixed(2)} / {item.unit}
                    </Typography>
                    <Typography sx={{ fontSize: '11.5px', color: '#756B63' }}>
                      MOQ: {item.quantity} {item.unit}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '12.5px', fontWeight: 600, color: '#b45309' }}>
                    {item.validTo}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', color: '#5F4B3B' }}>
                    {item.shipmentTerm}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        bgcolor: item.status.includes('Approved') ? '#dcfce7' : '#eff6ff',
                        color: item.status.includes('Approved') ? '#15803d' : '#1d4ed8',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedQuote(item)}
                      startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                      sx={{
                        borderColor: '#D6CEC4',
                        color: '#5F4B3B',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '11.5px',
                        '&:hover': { borderColor: '#A77B58', bgcolor: 'rgba(167, 123, 88, 0.05)' },
                      }}
                    >
                      View Quote
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Quote Detail Dialog */}
      <Dialog
        open={Boolean(selectedQuote)}
        onClose={() => setSelectedQuote(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        {selectedQuote && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedQuote.quotationCode}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63' }}>Supplier: {selectedQuote.company}</Typography>
              </Box>
              <IconButton onClick={() => setSelectedQuote(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: '#E8DFD5' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#3B3027', mb: 1 }}>
                {selectedQuote.productName}
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#FAF6F0', borderRadius: 2, mb: 2 }}>
                <Typography sx={{ fontSize: '14px', color: '#059669', fontWeight: 800 }}>
                  Quoted Price: {selectedQuote.currency} {selectedQuote.price.toFixed(2)} per {selectedQuote.unit}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.8 }}>
                  <strong>Order Quantity:</strong> {selectedQuote.quantity} {selectedQuote.unit}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Total Value:</strong> {selectedQuote.currency} {(selectedQuote.price * selectedQuote.quantity).toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Validity:</strong> Until {selectedQuote.validTo}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Shipment Terms:</strong> {selectedQuote.shipmentTerm}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Payment Terms:</strong> {selectedQuote.paymentTerm}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  <strong>Supplier Contact:</strong> {selectedQuote.businessEmail} ({selectedQuote.phone})
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setSelectedQuote(null)} sx={{ textTransform: 'none' }}>
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => toast.success(`Downloading formal quotation sheet ${selectedQuote.quotationCode}...`)}
                sx={{
                  bgcolor: '#A77B58',
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#936948' },
                }}
              >
                Download Quotation PDF
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
